import { useState } from "react";
import {
  Alert,
  Box,
  Chip,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import PersistedDataGrid from "../../components/PersistedDataGrid/PersistedDataGrid";
import useAuth from "../../hooks/useAuth";
import { apiVariable } from "../../api/apiVariable";
import { TimeOffPolicyEnum } from "../../enums/HR/TimeOffPolicyEnum";

interface TimeOffPolicyBreakdownDto {
  policy: keyof typeof TimeOffPolicyEnum;
  days: number;
}

interface EmployeeMonthlyTimeOffSummaryDto {
  employeeId: string;
  firstName: string;
  lastName: string;
  department: string;
  year: number;
  month: number;
  totalWorkingDays: number;
  totalTimeOffDays: number;
  workedDays: number;
  timeOffByPolicy: TimeOffPolicyBreakdownDto[];
}

const fetchWorkedTimeSummary = async (
  token: string | null | undefined,
  month: number,
  year: number,
) => {
  const { data } = await axios.get(
    `${apiVariable}/employeesTimeOffMonthlySummary`,
    {
      headers: { Authorization: `Bearer ${token}` },
      params: { month, year },
    },
  );

  return data as EmployeeMonthlyTimeOffSummaryDto[];
};

const WorkedTimeSummaryPage = () => {
  const { token, role } = useAuth();
  const today = new Date();
  const [selectedMonth, setSelectedMonth] = useState(today.getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(today.getFullYear());

  const years = Array.from(
    { length: 8 },
    (_, i) => today.getFullYear() - 3 + i,
  );

  const {
    data = [],
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["worked-time-summary", selectedMonth, selectedYear],
    queryFn: () => fetchWorkedTimeSummary(token, selectedMonth, selectedYear),
    enabled: !!token && role?.includes("HR"),
  });

  const columns: GridColDef[] = [
    {
      field: "employee",
      headerName: "Employee",
      flex: 1.3,
      valueGetter: (_value, row) => `${row.firstName} ${row.lastName}`.trim(),
    },
    {
      field: "department",
      headerName: "Department",
      flex: 1,
      valueGetter: (value) => value || "-",
    },
    {
      field: "totalWorkingDays",
      headerName: "Total Working Days",
      type: "number",
      flex: 0.8,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "totalTimeOffDays",
      headerName: "Time Off Days",
      type: "number",
      flex: 0.7,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "workedDays",
      headerName: "Worked Days",
      type: "number",
      flex: 0.7,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "timeOffByPolicy",
      headerName: "Time Off by Policy",
      flex: 2,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const policies =
          (params.row.timeOffByPolicy as TimeOffPolicyBreakdownDto[]) || [];

        if (policies.length === 0) {
          return <Typography variant="body2">-</Typography>;
        }

        return (
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {policies.map((item) => (
              <Chip
                key={`${params.row.employeeId}-${item.policy}`}
                size="small"
                label={`${TimeOffPolicyEnum[item.policy] || item.policy}: ${item.days}`}
              />
            ))}
          </Stack>
        );
      },
    },
  ];

  return (
    <Box sx={{ p: { xs: 0, sm: 3 }, minWidth: 0 }}>
      <Typography variant="h4" gutterBottom>
        Worked Time Monthly Summary
      </Typography>

      <br />

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth>
            <InputLabel id="month-select-label">Month</InputLabel>
            <Select
              labelId="month-select-label"
              label="Month"
              value={selectedMonth}
              onChange={(event) => setSelectedMonth(Number(event.target.value))}
            >
              {Array.from({ length: 12 }, (_, idx) => idx + 1).map((month) => (
                <MenuItem key={month} value={month}>
                  {new Date(2000, month - 1, 1).toLocaleString("en-US", {
                    month: "long",
                  })}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth>
            <InputLabel id="year-select-label">Year</InputLabel>
            <Select
              labelId="year-select-label"
              label="Year"
              value={selectedYear}
              onChange={(event) => setSelectedYear(Number(event.target.value))}
            >
              {years.map((year) => (
                <MenuItem key={year} value={year}>
                  {year}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {isError ? (
        <Alert severity="error">Failed to load worked time summary.</Alert>
      ) : null}

      <PersistedDataGrid
        rows={data.map((row) => ({ id: row.employeeId, ...row }))}
        columns={columns.map((column) => ({
          ...column,
          minWidth:
            column.field === "timeOffByPolicy"
              ? 300
              : column.field === "employee"
                ? 220
                : 160,
        }))}
        mobileCompactView
        compactViewConfig={{
          primaryField: "employee",
          getCardTitle: (row) => `${row.firstName} ${row.lastName}`,
          secondaryFields: ["department"],
          detailFields: [
            "totalWorkingDays",
            "totalTimeOffDays",
            "workedDays",
            "timeOffByPolicy",
          ],
        }}
        loading={isLoading}
        getRowHeight={() => "auto"}
        sx={{
          height: "calc(100vh - 280px)",
          minHeight: 420,
          maxHeight: 900,
          "& .MuiDataGrid-cell": {
            py: 1,
            alignItems: "center",
          },
        }}
        tableName="worked-time-summary"
      />
    </Box>
  );
};

export default WorkedTimeSummaryPage;
